import { IComplexId } from '@/shared/models';
import { IElection } from '@/shared/models/election';
import _ from 'lodash';
import { ICandidatureElectionVote } from '../models/candidature-election-vote';

export type VoteErrorType = 'VoteIsMissing' | 'MinNumberOfCandidatures' | 'MaxNumberOfCandidatures' | 'BlankVotingIsDisabled' | 'UniqueCandidatesIsRequired' | 'EqualGenderDivisionRequired';

export interface IVoteValidationError {

    electionId: IComplexId;

    type: VoteErrorType;
}

export class VoteValidationService {

    public validate(election: IElection, votes: ICandidatureElectionVote[]): IVoteValidationError[] {

        const errors: IVoteValidationError[] = [];

        if (election.elections && election.elections.length > 0) {

            election.elections.forEach(x =>  {

                errors.push(...this.validateCandidacyElection(x, votes));
            });
        } else {

            errors.push(...this.validateCandidacyElection(election, votes));
        }

        if (election.requireUniqueCandidatesPerBallot === true) {

            errors.push(... this.validateUniqueCandidatesIsRequired(election, votes));
        }

        return errors;
    }

    private validateCandidacyElection(election: IElection, votes: ICandidatureElectionVote[]): IVoteValidationError[] {

        const errors: IVoteValidationError[] = [];

        const vote = votes.find(x => x.electionId === election.electionId);

        if (!vote || vote === null) {

            errors.push({
                electionId: election.electionId,
                type: 'VoteIsMissing'
            });

            return errors;
        }

        if (vote.isBlankVote) {

            if (!election.enableBlankVote) {

                errors.push({
                    electionId: election.electionId,
                    type: 'BlankVotingIsDisabled'
                });
            }

            return errors;
        }

        const nbrOfCandidatures = vote && vote.candidatures ? vote.candidatures.length : 0;

        if (election.minNumberOfCandidatures && nbrOfCandidatures < election.minNumberOfCandidatures) {

            errors.push({
                electionId: election.electionId,
                type: 'MinNumberOfCandidatures'
            });
        }

        if (election.maxNumberOfCandidatures && nbrOfCandidatures > election.maxNumberOfCandidatures) {

            errors.push({
                electionId: election.electionId,
                type: 'MaxNumberOfCandidatures'
            });
        }

        if (election.requiredEqualGenderDivision === true) {

            const totalNbrOfVotes = (vote.candidatures) ? vote.candidatures.length : 0;

            const ballotGenderStatistics = _.countBy(vote.candidatures, (x) => x && x.userData ? x.userData.gender : 'Undefined');

            const electionGenderStatistics = vote.validationSupport && vote.validationSupport.electionGenderStatistics
                ? vote.validationSupport.electionGenderStatistics
                : {};

            const nbrOfGendersInElection = Object.keys(electionGenderStatistics).length;

            if (nbrOfGendersInElection !== 0) {

                const minOfEachGender = Math.floor(totalNbrOfVotes / nbrOfGendersInElection);
                const maxOfEachGender = Math.ceil(totalNbrOfVotes / nbrOfGendersInElection);

                let isInvalid = false;

                for (const key of Object.keys(electionGenderStatistics)) {

                    const genderStatInBallot = ballotGenderStatistics[key] !== undefined ? ballotGenderStatistics[key] : 0;

                    if (genderStatInBallot < minOfEachGender || genderStatInBallot > maxOfEachGender) {
                        isInvalid = true;
                    }
                }

                if (isInvalid) {
                    errors.push({
                        electionId: election.electionId,
                        type: 'EqualGenderDivisionRequired'
                    });
                }
            }
        }

        return errors;
    }

    private validateUniqueCandidatesIsRequired(election: IElection, votes: ICandidatureElectionVote[]): IVoteValidationError[] {

        const errors: IVoteValidationError[] = [];

        const allCandidates: string[] = [];

        votes.forEach(x => {

            if (x !== undefined && x.candidatures !== undefined && x.candidatures.length > 0) {

                allCandidates.push(... x.candidatures.map(y => {

                    return y.id.split('/')[2];
                }));
            }
        });

        const hasDuplicates = _.uniq(allCandidates).length !== allCandidates.length;

        if (hasDuplicates) {

            errors.push({
                electionId: election.electionId,
                type: 'UniqueCandidatesIsRequired'
            });
        }

        return errors;
    }
}
